import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
}

function TokenBreakdownTooltip({ promptTokens, completionTokens, totalTokens }) {
  return (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
          Input tokens
        </Typography>
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
          {promptTokens.toLocaleString()}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
          Output tokens
        </Typography>
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
          {completionTokens.toLocaleString()}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 0.5 }}
      >
        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
          Total
        </Typography>
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
          {totalTokens.toLocaleString()}
        </Typography>
      </Stack>
    </Stack>
  );
}

/**
 * @param {{
 *   modelName?: string,
 *   selectedModelArn?: string,
 *   availableModels?: Array<{value: string, label: string, description?: string}>,
 *   onModelChange?: (arn: string) => void,
 *   messageCount?: number,
 *   promptTokens?: number,
 *   completionTokens?: number,
 *   totalTokens?: number,
 *   compact?: boolean
 * }} props
 */
export function ChatTokenStatusBar({
  modelName = 'Default',
  selectedModelArn = '',
  availableModels = [],
  onModelChange,
  messageCount = 0,
  promptTokens = 0,
  completionTokens = 0,
  totalTokens = 0,
  compact = false,
}) {
  const hasModels = availableModels.length > 0;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  /** @param {React.MouseEvent<HTMLButtonElement>} event */
  const handleOpen = useCallback((event) => {
    if (hasModels) setAnchorEl(event.currentTarget);
  }, [hasModels]);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  /** @param {string} value */
  const handleSelect = useCallback((value) => {
    onModelChange?.(value);
    handleClose();
  }, [onModelChange, handleClose]);

  const selectedModel = availableModels.find((m) => m.value === selectedModelArn);
  const selectedLabel = selectedModel ? selectedModel.label : modelName;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: compact
          ? 'minmax(0, 3fr) auto minmax(0, 1fr)'
          : 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: 1,
        mt: 0.75,
        px: 0.5,
        color: 'text.disabled',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ...(compact && { flex: 1 }) }}>
        <Iconify icon="solar:cpu-bolt-bold" width={13} />
        {hasModels ? (
          <>
            <Button
              onClick={handleOpen}
              size="small"
              endIcon={
                <Iconify
                  icon={open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                  width={12}
                />
              }
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                height: 20,
                px: 0.75,
                py: 0,
                fontSize: '0.75rem',
                fontWeight: 400,
                color: 'text.disabled',
                bgcolor: 'transparent',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.75,
                lineHeight: 1,
                textTransform: 'none',
                ...(compact && { flex: '0 1 auto', justifyContent: 'space-between', mr: 1.5 }),
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'text.disabled',
                  color: 'text.secondary',
                },
                '& .MuiButton-endIcon': { ml: 0.25, '& svg': { fontSize: '12px !important' } },
              }}
            >
              <Box
                component="span"
                sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {selectedLabel}
              </Box>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 200,
                    maxWidth: 320,
                    mt: -0.5,
                  },
                },
              }}
            >
              {availableModels.map((model) => (
                <MenuItem
                  key={model.value}
                  selected={model.value === selectedModelArn}
                  onClick={() => handleSelect(model.value)}
                  sx={{ alignItems: 'flex-start', flexDirection: 'column', px: 1.5, py: 1 }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.4 }}>
                    {model.label}
                  </Typography>
                  {model.description && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.3 }}>
                      {model.description}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Typography variant="caption" noWrap>
            Model: {modelName}
          </Typography>
        )}
      </Stack>

      {!compact && (
        <Tooltip title="Messages in this conversation" placement="top">
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ gridColumn: '2', justifySelf: 'center', cursor: 'default' }}
          >
            <Iconify icon="solar:chat-round-line-bold" width={13} />
            <Typography variant="caption" noWrap>
              {messageCount} {messageCount === 1 ? 'Message' : 'Messages'}
            </Typography>
          </Stack>
        </Tooltip>
      )}

      <Tooltip
        title={
          <TokenBreakdownTooltip
            promptTokens={promptTokens}
            completionTokens={completionTokens}
            totalTokens={totalTokens}
          />
        }
        placement="top"
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ gridColumn: '3', justifySelf: 'end', cursor: 'default' }}
        >
          <Iconify icon="solar:bolt-bold" width={13} />
          <Typography variant="caption" noWrap>
            {formatTokens(totalTokens)} Tokens
          </Typography>
        </Stack>
      </Tooltip>
    </Box>
  );
}
